import React from "react";
import { ExerciseDetails } from "../../api/types";
import { useDialog } from "../DialogManager.tsx";
import { isNotNullish } from "../../util/typeGuards.ts";
import { getAverageStats } from "../../util/exercise.tsx";
import { ExerciseSetButton } from "../widgets/ExerciseSetButton.tsx";

interface WorkoutExerciseSetsProps {
  exercise: ExerciseDetails;
  workoutExerciseIndex?: number;
  onAddSet?: (
    workoutExerciseIndex: number,
    repetitions: number,
    resistance: number,
  ) => void;
  onEditSet?: (
    workoutExerciseIndex: number,
    setIndex: number,
    repetitions: number,
    resistance: number,
  ) => void;
}

const WorkoutExerciseSets: React.FC<WorkoutExerciseSetsProps> = ({
  exercise,
  workoutExerciseIndex,
  onAddSet,
  onEditSet,
}) => {
  const { openDialog } = useDialog();

  const clickable = isNotNullish(workoutExerciseIndex) && onAddSet && onEditSet;

  const handleOpenDialog = (e: React.MouseEvent, setIndex?: number) => {
    if (!clickable) {
      return;
    }

    e.stopPropagation();

    const set = setIndex !== undefined ? exercise.sets[setIndex] : null;

    // Pre-fill with last set values if available
    const dialogProps: any = {
      resistanceUnit: exercise.resistance_unit,
      exercise: exercise,
      onAddSet: (repetitions: number, resistance: number) =>
        onAddSet(workoutExerciseIndex, repetitions, resistance),
      onEditSet: (repetitions: number, resistance: number) =>
        onEditSet(
          workoutExerciseIndex,
          setIndex ?? -1,
          repetitions,
          resistance,
        ),
    };

    if (set) {
      dialogProps.repetitions = set.repetitions.toString();
      dialogProps.resistance = set.resistance.toString();
      dialogProps.editMode = true;
    } else if (exercise.sets.length > 0) {
      const lastSet = exercise.sets[exercise.sets.length - 1];
      dialogProps.repetitions = lastSet.repetitions.toString();
      dialogProps.resistance = lastSet.resistance.toString();
    }

    openDialog(isNotNullish(setIndex) ? "edit-set" : "add-set", dialogProps);
  };

  return (
    <>
      <div className="pt-2 flex flex-wrap gap-2 justify-start items-center">
        {exercise.sets.map((set, setIndex) => (
          <ExerciseSetButton
            key={setIndex}
            onClick={
              clickable ? (e) => handleOpenDialog(e, setIndex) : undefined
            }
            isEditable
          >
            <span className="text-nowrap">
              {set.repetitions} × {Math.round(set.resistance)}
              {exercise.resistance_unit}
            </span>
          </ExerciseSetButton>
        ))}

        {clickable && (
          <ExerciseSetButton
            onClick={handleOpenDialog}
            isEditable
            isAddButton
          />
        )}
      </div>
      <p className="text-xs text-gray-600 mt-4 sm:justify-self-end">
        {getAverageStats(exercise) || "No sets recorded"}
      </p>
    </>
  );
};

export { WorkoutExerciseSets };
